import React, { useCallback } from "react";
import Highlight from "react-highlighter";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

import clsx from "clsx";
import { bindActionCreators, Dispatch } from "redux";

import { useLocalization, TranslatedText } from "@plex/culture-react";
import { DataTableDef, SelectionMode, Boolean } from "@plex/react-components";

import { commonMessages } from "infrastructure/localization";
import { InlineEditProvider, InlineEdit } from "ui/components";
import { useLocalizedValues, useUserContext } from "ui/hooks";
import { IChangeToken } from "ui/screens/translation-managers/shared/types/Token.types";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";

import { plexTranslationMessagesActions } from "../PlexTranslationMessages.actions";
import { IPlexTranslationMessageRow, IPlexTranslationMessagesManagerState, IChangeText } from "../PlexTranslationMessages.state";

import styles from "./PlexTranslationMessagesGrid.module.scss";
import { PlexTranslationMessagesTokenValueItem } from "./PlexTranslationMessagesTokenValueItem";

export interface IPlexTranslationMessagesGridProps {
  messageFilter?: string;
  textFilter?: string;
  showPreviewText?: boolean;
  translations: IPlexTranslationMessageRow[];
  selected: IPlexTranslationMessageRow[];
  isSearched: boolean;
  changePlexTranslation: (change: IChangeText) => void;
  changeTokenValue: (change: IChangeToken) => void;
  selectPlexTranslations: (selected: IPlexTranslationMessageRow[]) => void;
}

export const PlexTranslationMessagesManagerGrid: React.FC<IPlexTranslationMessagesGridProps> = ({
  messageFilter,
  textFilter,
  showPreviewText,
  translations,
  selected,
  isSearched,
  changePlexTranslation,
  changeTokenValue,
  selectPlexTranslations
}) => {
  const { t } = useLocalization();

  const localizedValues = useLocalizedValues(() => {
    return {
      messageId: displayMessages.property("messageId"),
      sourceText: displayMessages.property("sourceText"),
      translatedText: displayMessages.property("translatedText"),
      textPreviewTokenValues: displayMessages.property("textPreviewTokenValues"),
      textPreview: displayMessages.property("textPreview"),
      status: displayMessages.property("status"),
      parent: displayMessages.property("parent"),
      assignedTo: displayMessages.property("assignedTo"),
      refineSearch: commonMessages.info("refineSearch"),
      startSearch: commonMessages.info("startSearch")
    };
  });

  const userContext = useUserContext();
  const roles = userContext.getRoleAccessData();

  const keySelector = useCallback((row: IPlexTranslationMessageRow) => row.data.messageId, []);

  return (
    <InlineEditProvider>
      <DataTableDef<IPlexTranslationMessageRow>
        className={clsx(styles.base, showPreviewText ? styles.showTextPreview : styles.notShowTextPreview)}
        data={translations}
        emptyText={isSearched ? localizedValues.refineSearch : localizedValues.startSearch}
        keySelector={keySelector}
        onSelectionChanged={selectPlexTranslations}
        selected={selected}
        selectionMode={SelectionMode.multiple}
      >
        <DataTableDef.Column<IPlexTranslationMessageRow> id="column1" key="1" title={localizedValues.messageId} valueSelector={r => r.data.messageId} sortable>
          {row => (
            <Link to={`/message-details/${row.data.messageId}`}>
              <Highlight search={messageFilter || ""}>{row.data.messageId}</Highlight>
            </Link>
          )}
        </DataTableDef.Column>

        <DataTableDef.Column<IPlexTranslationMessageRow> id="column2" key="2" title={localizedValues.sourceText} valueSelector={r => r.data.sourceText} sortable>
          {row => <Highlight search={textFilter || ""}>{row.data.sourceText}</Highlight>}
        </DataTableDef.Column>

        <DataTableDef.Column<IPlexTranslationMessageRow> id="column3" key="3" title={localizedValues.translatedText} valueSelector={r => r.data.translatedText} sortable>
          {row => (
            <InlineEdit<IPlexTranslationMessageRow>
              initialValue={row.data.translatedText}
              originalValue={row.originalData && row.originalData.translatedText}
              onValueChanged={val => {
                changePlexTranslation({ text: val, index: row.index });
              }}
              isChanged={row.isTranslationChanged}
              validationIssues={row.validationIssues}
              validationTags={["Translation"]}
              className={styles.inlineEdit}
              canEdit={() => roles.LocalizationManager || roles.PlexLocalizationManager}
              context={row}
            >
              <Highlight search={textFilter || ""}>{row.data.translatedText}</Highlight>
            </InlineEdit>
          )}
        </DataTableDef.Column>

        {!!showPreviewText && (
          <DataTableDef.Column<IPlexTranslationMessageRow> id="column4" key="4" title={localizedValues.textPreviewTokenValues}>
            {row => (
              <div className={styles.groupTokens}>
                {row.data.tokens.map((item, index) => {
                  return <PlexTranslationMessagesTokenValueItem key={index} token={item} rowIndex={row.index} tokenIndex={index} changeTokenValue={changeTokenValue} />;
                })}
              </div>
            )}
          </DataTableDef.Column>
        )}

        {!!showPreviewText && (
          <DataTableDef.Column<IPlexTranslationMessageRow> id="column5" key="5" title={localizedValues.textPreview} valueSelector={r => r.data.textPreview} sortable>
            {row => <p className={styles.wrapText}>{row.data.textPreview}</p>}
          </DataTableDef.Column>
        )}

        <DataTableDef.Column<IPlexTranslationMessageRow>
          id="column6"
          key="6"
          title={localizedValues.status}
          valueSelector={r => r.data.translationStatus && t(r.data.translationStatus)}
          sortable
        >
          {row => row.data.translationStatus && <TranslatedText messageId={row.data.translationStatus} />}
        </DataTableDef.Column>

        <DataTableDef.Column<IPlexTranslationMessageRow>
          id="column7"
          key="7"
          className={styles.parentFlag}
          title={localizedValues.parent}
          valueSelector={r => r.data.isParentMessage}
          sortable
        >
          {row => <Boolean value={row.data.isParentMessage} />}
        </DataTableDef.Column>
        <DataTableDef.Column<IPlexTranslationMessageRow> id="column8" key="8" title={localizedValues.assignedTo} valueSelector={r => r.data.assignedTo} sortable>
          {row => row.data.assignedTo}
        </DataTableDef.Column>
      </DataTableDef>
    </InlineEditProvider>
  );
};

/* istanbul ignore next */
export default connect(
  (state: { plexTranslationMessagesManager: IPlexTranslationMessagesManagerState }) => {
    const filter = state.plexTranslationMessagesManager.filter;
    const grid = state.plexTranslationMessagesManager.grid;
    return {
      messageFilter: filter.lastData && filter.lastData.filterMessageId,
      textFilter: filter.lastData && filter.lastData.filterText,
      showPreviewText: filter.lastData && filter.lastData.isCheckedShowPreviewText,
      translations: grid.gridData,
      selected: grid.selected,
      isSearched: grid.isSearched
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        changePlexTranslation: plexTranslationMessagesActions.changePlexTranslation,
        changeTokenValue: plexTranslationMessagesActions.changeTokenValue,
        selectPlexTranslations: plexTranslationMessagesActions.selectPlexTranslations
      },
      dispatch
    );
  }
)(PlexTranslationMessagesManagerGrid);
